import type { ComponentType } from 'react';
import type { AgentProposalDto } from '@immoaktas/shared';

/**
 * How a proposal card looks for one tool (docs/17 §Neues Werkzeug hinzufügen). Every proposal renders with the
 * generic card (title, summary, warnings, fields to check and edit, Übernehmen / Ablehnen / Rückgängig); a tool only
 * needs a view when it wants more, e.g. a preview of a letter or a small table.
 *
 * Add a view as ONE new file `views/<tool>.tsx` exporting `view` (no list to edit, so parallel work never conflicts):
 *
 *   export const view: ProposalView = { tools: ['propose_rent_change'], Details: ({ p }) => <RentPreview p={p} /> };
 */
export type ProposalView = {
  /** tool names (`AgentProposalDto.tool`) this view is for */
  tools: string[];
  /** extra content between the fields and the actions */
  Details?: ComponentType<{ p: AgentProposalDto; open: boolean }>;
  /** fields not shown as rows (the Details render them themselves); still editable through the card */
  hideFieldKeys?: string[];
};

const modules = import.meta.glob<{ view: ProposalView }>('./views/*.tsx', { eager: true });
const BY_TOOL = new Map<string, ProposalView>();
for (const [file, m] of Object.entries(modules)) {
  for (const tool of m.view?.tools ?? []) {
    if (BY_TOOL.has(tool)) throw new Error(`proposal view for ${tool} defined twice (${file})`);
    BY_TOOL.set(tool, m.view);
  }
}

const DEFAULT: ProposalView = { tools: [] };
export const viewFor = (tool: string): ProposalView => BY_TOOL.get(tool) ?? DEFAULT;
